import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "redux/store";
import { useNavigate, useParams } from "react-router-dom";
import { clearStaffDetails, getStaffDetails } from "redux/Slice/staffDetails";
import "./style.css";
import { RootState } from "redux/store";
import Header from "common/Header";
import { getErrorMessage, getImageUrl } from "utils";
import { Icons } from "utils/iconPath";
import Button from "common/commonButton";
import { CREATE_SUPPLIER } from "redux/services/apiTypes";
import apiCall from "redux/services/apiCall";
import { useCallback } from "react";
import toast from "react-hot-toast";
import ManageAvailabilityCard from "components/profile/manageAvailability/manageAvailabilityCard/ManageAvailabilityCard";
import ManageAvailabilityModal from "./ManageAvailabilityModal";

const StaffDetails: FC = (): JSX.Element => {
  // Hooks
  const { t } = useTranslation("staffDetails");
  const dispatch = useDispatch<AppDispatch>();
  const param = useParams();
  const navigate = useNavigate();

  // Global States
  const { staffDetails, supplierProfile } = useSelector(
    (state: RootState) => ({
      staffDetails: state.staffDetails,
      supplierProfile: state.supplierProfile,
    }),
    shallowEqual
  );

  // Local States
  const [availabilities, setAvailabilities] = useState<
    {
      id: number;
      day: string;
      start: string;
      end: string;
    }[]
  >([]);
  const [selectedAvailability, setSelectedAvailability] = useState<any>(null);
  const [availabilityModalMode, setAvailabilityModalMode] = useState<
    "add" | "edit"
  >("add");
  const [
    manageAvailabilityModalVisibility,
    setManageAvailabilityModalVisibility,
  ] = useState<boolean>(false);

  // Variables
  const supplierId = Number(supplierProfile?.supplierProfileRes?.id);
  const staffId = Number(param?.staffId);

  // Functions
  const getAvailability = useCallback(async () => {
    const apiData = {
      type: `${CREATE_SUPPLIER}staff/${staffId}/availability/`,
      apiType: "GET",
    };
    try {
      let response = await apiCall(apiData);
      setAvailabilities(response);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [staffId]);

  // Effects
  useEffect(() => {
    if (supplierId && staffId) {
      dispatch(
        getStaffDetails({
          staffId: staffId,
          query: "",
        })
      );
      getAvailability();
    }

    return () => {
      dispatch(clearStaffDetails());
    };
  }, [supplierId, staffId, dispatch, getAvailability]);

  return (
    <>
      <div className="StaffDetails">
        <Header
          Heading={t("staff_id") as string}
          id={` #${staffId}`}
          isBack={true}
          isButton={true}
          ButtonTitle={t("edit_staff") as string}
          buttonOnClick={() => {
            navigate("/staff/update", { state: staffDetails?.response });
          }}
        />
        <div className="card infoCard">
          <img
            src={
              getImageUrl(staffDetails?.response?.profile_picture) ||
              Icons.DUMMY_PROFILE
            }
            alt="profile"
            className="photo"
          />
          <div className="content">
            <h2 className="heading2">{staffDetails?.response?.name}</h2>
            <p className="bodyText">{staffDetails?.response?.phone}</p>
          </div>
        </div>

        <div className="card availabilitiesCard">
          <div className="headingContainer">
            <h4 className="heading3">{t("availabilities")}</h4>
            <Button
              variant={"secondary"}
              title={t("add_new_availability")}
              onClick={() => {
                setAvailabilityModalMode("add");
                setManageAvailabilityModalVisibility(true);
              }}
            />
          </div>

          <div className="sections">
            {availabilities?.length ? (
              availabilities?.map(
                (
                  availability: {
                    id: number;
                    day: string;
                    start: string;
                    end: string;
                  },
                  index: number
                ) => {
                  return (
                    <ManageAvailabilityCard
                      key={`availability-card-key-${availability?.id}-${index}`}
                      heading={availability?.day}
                      start={availability?.start}
                      end={availability?.end}
                      day_off={false}
                      containerStyle={{
                        backgroundColor: "rgb(var(--off-white))",
                        maxWidth: "fit-content",
                      }}
                      onClick={() => {
                        setSelectedAvailability(availability);
                        setManageAvailabilityModalVisibility(true);
                        setAvailabilityModalMode("edit");
                      }}
                    />
                  );
                }
              )
            ) : (
              <p className="description">No availabilities found!</p>
            )}
          </div>
        </div>

        <div className="card categoriesCard">
          <div className="headingContainer">
            <h4 className="heading3">{t("categories")}</h4>
            <Button
              variant={"secondary"}
              title={t("add_new_category")}
              onClick={() => {
                navigate("/staff/update", { state: staffDetails?.response });
              }}
            />
          </div>

          <div className="sections">
            {staffDetails?.response?.services?.length ? (
              staffDetails?.response?.services?.map(
                (category: any, index: number) => {
                  return (
                    <p
                      key={`assigned-category-${category?.id}-${index}`}
                      className="section bodyText"
                    >
                      {category?.name}
                    </p>
                  );
                }
              )
            ) : (
              <p className="description">No assigned categories found!</p>
            )}
          </div>
        </div>
      </div>

      {manageAvailabilityModalVisibility ? (
        <ManageAvailabilityModal
          mode={availabilityModalMode}
          visibility={manageAvailabilityModalVisibility}
          editAvailabilityData={selectedAvailability}
          onClose={() => {
            setManageAvailabilityModalVisibility(false);
            setSelectedAvailability(null);
          }}
          onSuccess={() => {
            getAvailability();
          }}
          staffId={staffId}
        />
      ) : null}
    </>
  );
};

export default StaffDetails;
